import { useEffect, useRef, useState } from 'react';

export function useReveal({ once = true, threshold = 0.12 } = {}) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          if (once) io.unobserve(el);
        } else if (!once) {
          setSeen(false);
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, threshold]);
  return [ref, seen];
}

export function useCountUp(target, { duration = 1400 } = {}) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const [inView, setInView] = useState(false);
  const fromRef = useRef(0);
  const rafRef = useRef(0);

  // Detect when the element scrolls into view (separate from the animation so a
  // late-arriving `target` can still trigger a fresh count-up).
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.unobserve(el);
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animate to `target` once visible, and re-run whenever `target` changes
  // (e.g. when the real numbers arrive from the API after the first paint).
  useEffect(() => {
    if (!inView) return undefined;
    const from = fromRef.current;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - (1 - t) ** 3;
      const next = Math.round(from + (target - from) * eased);
      fromRef.current = next;
      setVal(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [inView, target, duration]);

  return [ref, val];
}

export function useFollowMouse() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -1000, y: -1000, inside: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      setPos({ x: e.clientX - r.left, y: e.clientY - r.top, inside: true });
    };
    const onLeave = () => setPos({ x: -1000, y: -1000, inside: false });
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);
  return [ref, pos];
}

// Cycles through a list of strings on an interval — used for the hero search's
// rotating placeholder (T2B-390). Resets to the first item whenever the list
// changes (e.g. switching the events/brands tab) and pauses when disabled.
export function useRotatingText(items, { interval = 3000, enabled = true } = {}) {
  const list = Array.isArray(items) ? items : [];
  const key = list.join('|');
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    if (!enabled || list.length <= 1) return undefined;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % list.length);
    }, interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, interval]);
  return list.length ? list[index % list.length] : '';
}

export const cx = (...a) => a.filter(Boolean).join(' ');
